import React, { useState, useEffect, useRef } from 'react';
import { Briefcase, Trash2 } from 'lucide-react';
import { actividadService } from '../services/actividadService';
import { ActividadEconomica } from '../lib/supabase';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useLumisial } from '../context/LumisialContext';
import { calcularEstadoAutomatico, getColorEstado, getTextoEstado } from '../utils/actividadUtils';
import { DialogShell } from './ui/DialogShell';
import { ModalHeader } from './ui/ModalHeader';
import { OwnerContext } from '../types/actividad';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface ActividadModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly onDelete?: () => void;
  readonly actividad?: ActividadEconomica;
  readonly ownerContext?: OwnerContext;
}

export const ActividadModal: React.FC<ActividadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onDelete,
  actividad,
  ownerContext
}) => {
  const { selectedLumisial } = useLumisial();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    fecha_inicio: '',
    fecha_fin: '',
    estado: 'planificacion' as 'planificacion' | 'en_curso' | 'finalizada'
  });

  const isEditing = !!actividad;

  // Función para verificar si hay cambios sin guardar
  const hasFormChanges = () => {
    if (!isEditing) {
      // Para nueva actividad, verificar si hay algún campo con contenido
      return formData.nombre !== '' ||
             formData.descripcion !== '' ||
             formData.fecha_inicio !== '' ||
             formData.fecha_fin !== '';
    }

    // Para edición, comparar con datos originales
    const originalData = {
      nombre: actividad.nombre,
      descripcion: actividad.descripcion || '',
      fecha_inicio: actividad.fecha_inicio ? actividad.fecha_inicio.split('T')[0] : '',
      fecha_fin: actividad.fecha_fin ? actividad.fecha_fin.split('T')[0] : '',
      estado: (actividad.estado === 'archivada' ? 'finalizada' : actividad.estado) as 'planificacion' | 'en_curso' | 'finalizada'
    };

    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  // El aviso de "cambios sin guardar" lo gestiona DialogShell vía hasUnsavedChanges
  const hasUnsavedChanges = loading || error !== '' || showDeleteConfirm || hasFormChanges();
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  useEffect(() => {
    if (actividad) {
      setFormData({
        nombre: actividad.nombre,
        descripcion: actividad.descripcion || '',
        fecha_inicio: actividad.fecha_inicio ? actividad.fecha_inicio.split('T')[0] : '',
        fecha_fin: actividad.fecha_fin ? actividad.fecha_fin.split('T')[0] : '',
        estado: (actividad.estado === 'archivada' ? 'finalizada' : actividad.estado) as 'planificacion' | 'en_curso' | 'finalizada'
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        fecha_inicio: '',
        fecha_fin: '',
        estado: 'planificacion'
      });
    }
    handleError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleError se omite a propósito: incluirlo re-ejecutaría el efecto en cada render y reiniciaría el formulario
  }, [actividad, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    handleError('');
    setLoading(true);

    try {
      // Calcular estado automático según fechas
      const estadoAutomatico = calcularEstadoAutomatico(formData.fecha_inicio, formData.fecha_fin);

      if (actividad) {
        await actividadService.update(actividad.id, {
          ...formData,
          estado: estadoAutomatico
        });
      } else {
        // Resolver ownerContext: prop explícito > selectedLumisial del contexto > error
        const resolvedOwnerContext: OwnerContext = ownerContext
          ?? (selectedLumisial
            ? { tipo: 'lumisial', id: selectedLumisial.id, nombre: selectedLumisial.nombre }
            : (() => { throw new Error('No hay contexto de propietario'); })());

        await actividadService.create(
          {
            ...formData,
            estado: estadoAutomatico,
            total_ingresos: 0,
            total_costos: 0,
            utilidad_real: 0,
            porcentaje_utilidad: 0
          },
          resolvedOwnerContext
        );
      }
      onSuccess();
      onClose();
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos guardar la actividad.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!actividad) return;

    handleError('');
    setDeleting(true);

    try {
      await actividadService.delete(actividad.id);
      setShowDeleteConfirm(false);
      onClose();

      if (onDelete) {
        onDelete();
      } else {
        onSuccess();
      }
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos eliminar la actividad.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  let submitLabel = 'Crear Actividad';
  if (loading) submitLabel = 'Guardando...';
  else if (actividad) submitLabel = 'Actualizar';

  return (
    <>
      <DialogShell
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        hasUnsavedChanges={hasUnsavedChanges}
        closeRef={closeRef}
        header={(
          <ModalHeader
            variant="gradient-blue"
            title={actividad ? 'Editar Actividad' : 'Nueva Actividad Económica'}
            subtitle={actividad ? 'Actualiza la información' : 'Registra una nueva actividad generadora de ingresos'}
            icon={<Briefcase className="w-6 h-6" />}
            onClose={requestClose}
          />
        )}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
            <FormErrorAlert error={error} onClear={clearError} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <TextInput
                  label="Nombre de la Actividad"
                  value={formData.nombre}
                  onChange={(v) => setFormData({ ...formData, nombre: v })}
                  placeholder="Ej: Pan Amasado, Cena Show, etc."
                  required
                />
              </div>

              <div className="md:col-span-2">
                <Textarea
                  label="Descripción"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  rows={3}
                  placeholder="Describe brevemente la actividad..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  Fecha de Inicio
                </label>
                <input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                  className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  Fecha de Fin
                </label>
                <input
                  type="date"
                  value={formData.fecha_fin}
                  onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                  min={formData.fecha_inicio || undefined}
                  className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                />
              </div>

              {isEditing && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Estado
                  </label>
                  <div className={`px-4 py-3 rounded-lg border font-medium ${getColorEstado(formData.estado)}`}>
                    {getTextoEstado(formData.estado)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    El estado se calcula automáticamente según las fechas de la actividad
                  </p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              {!isEditing ? (
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Nota:</span> La actividad se creará en estado "Planificación". El estado se actualizará automáticamente según las fechas.
                </p>
              ) : null}
              <p className="text-sm text-blue-800 mt-2">
                <span className="font-semibold">Utilidad Objetivo:</span> Se calcula automáticamente según los productos y gastos. La utilidad esperada se ajusta dinámicamente según el tiempo transcurrido.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={requestClose}
                className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitLabel}
              </button>
            </div>
        </form>
      </DialogShell>

      {showDeleteConfirm && (
        <DialogShell
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          level="nested"
          size="sm"
          header={
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Trash2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Confirmar Eliminación</h3>
                  <p className="text-red-100 text-sm">Esta acción no se puede deshacer</p>
                </div>
              </div>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-semibold mb-2">
                ¿Estás seguro de que deseas eliminar esta actividad?
              </p>
              <p className="text-red-700 text-sm">
                Se eliminaran todos los datos asociados: productos, pedidos, gastos y ventas registrados.
              </p>
            </div>

            {actividad && (
              <div className="bg-slate-100 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">Actividad a eliminar:</p>
                <p className="font-bold text-slate-900">{actividad.nombre}</p>
                {actividad.descripcion && (
                  <p className="text-sm text-slate-600 mt-1">{actividad.descripcion}</p>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogShell>
      )}
    </>
  );
};
